// src/components/TopicMafia/hooks/useTopicMafiaWS.js
import { useEffect, useRef, useCallback } from "react";
import { dedupeSay, makeWsUrl } from "../topicMafiaUi";

export default function useTopicMafiaWS({ roomId, fetchState, setState, setSays, onVoteResult }) {
    const wsRef = useRef(null);

    const fetchStateRef = useRef(fetchState);
    const setStateRef = useRef(setState);
    const setSaysRef = useRef(setSays);
    const onVoteResultRef = useRef(onVoteResult);

    useEffect(() => { fetchStateRef.current = fetchState; }, [fetchState]);
    useEffect(() => { setStateRef.current = setState; }, [setState]);
    useEffect(() => { setSaysRef.current = setSays; }, [setSays]);
    useEffect(() => { onVoteResultRef.current = onVoteResult; }, [onVoteResult]);

    const fetchTimerRef = useRef(null);
    const requestFetch = useCallback(() => {
        const fn = fetchStateRef.current;
        if (!fn) return;
        if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = setTimeout(() => {
            fetchTimerRef.current = null;
            fn();
        }, 120);
    }, []);

    // ✅ 공통: says는 "교체" 금지, merge만 허용
    const mergeSays = useCallback((incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0) return;
        setSaysRef.current?.((prev) => dedupeSay([...(prev || []), ...incoming]));
    }, []);

    useEffect(() => {
        if (!roomId) return;

        fetchStateRef.current?.();

        const url = makeWsUrl(roomId);
        let ws;

        try {
            ws = new WebSocket(url);
            wsRef.current = ws;
        } catch {
            return () => { };
        }

        ws.onmessage = (evt) => {
            try {
                const msg = JSON.parse(evt.data);

                // state snapshot
                if ((msg.type === "state" || msg.type === "game_state_update") && msg.payload) {
                    const next = msg.payload;

                    setStateRef.current?.((prev) => {
                        const keepMe = prev?.me;
                        return { ...next, me: next?.me ?? keepMe };
                    });

                    // ✅ says는 merge만
                    mergeSays(next?.says);

                    // ✅ 개인화 보정 (핵심!)
                    const status = next?.room?.status;
                    const myTopic = next?.me?.my_topic;

                    if (
                        (status === "PLAYING" || status === "VOTING" || status === "GUESS") &&
                        !myTopic
                    ) {
                        requestFetch();
                    }

                    return;
                }

                // say added
                if ((msg.type === "say" || msg.type === "game_say_added") && msg.payload) {
                    const p = msg.payload;
                    const s = {
                        id: p.id || `ws-${p.round}-${p.turn_index}-${p.player_id}`,
                        player_id: p.player_id,
                        round: p.round,
                        turn_index: p.turn_index,
                        text: p.text,
                        created_at: p.created_at || new Date().toISOString(),
                    };
                    setSaysRef.current?.((prev) => dedupeSay([...(prev || []), s]));
                    return;
                }

                // vote result
                if ((msg.type === "vote_result" || msg.type === "game_vote_result") && msg.payload) {
                    const voteBlock = msg.payload; // ✅ wrapper (state/result를 같이 들 수 있음)

                    // ✅ state는 wrapper에서 먼저 본다
                    const nextState = voteBlock.state ?? voteBlock.payload?.state ?? null;
                    if (nextState) {
                        setStateRef.current?.((prev) => {
                            const keepMe = prev?.me;
                            return { ...nextState, me: nextState?.me ?? keepMe };
                        });
                        mergeSays(nextState?.says);
                    } else {
                        requestFetch();
                    }

                    // ✅ "진짜 결과"는 wrapper에서 따로 추출
                    const result =
                        voteBlock.result ??
                        voteBlock.vote_result ??
                        voteBlock.data ??
                        voteBlock.payload ??
                        voteBlock; // 최후 fallback (그래도 wrapper 자체)

                    onVoteResultRef.current?.(result);
                    return;
                }
            } catch {
                // ignore
            }
        };

        ws.onerror = () => {
            requestFetch();
        };

        return () => {
            try { ws.close(); } catch { }
            wsRef.current = null;

            if (fetchTimerRef.current) {
                clearTimeout(fetchTimerRef.current);
                fetchTimerRef.current = null;
            }
        };
    }, [roomId, requestFetch, mergeSays]);

    return { wsRef };
}
